import { type ReactElement, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Button } from "../Button";
import { Spinner } from "../Spinner";

const supportedImageTypes = new Set(["image/jpeg", "image/png"]);

export type InputImageProps = {
  value?: string | null;
  onChange?: (url?: string) => void;
  onRemove?: () => void;
  uploadFile: (file: File) => Promise<string>;
  className?: string;
  previewClassName?: string;
};

export const InputImage = ({
  value,
  onChange,
  onRemove,
  uploadFile,
  className,
  previewClassName,
}: InputImageProps): ReactElement => {
  const [url, setUrl] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync local preview when the controlled value changes (adjust state during render).
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (value !== prevValue) {
    setPrevValue(value);
    setUrl(value);
  }

  const setFile = async (file: File | undefined) => {
    setError("");
    if (!file) {
      setUrl(undefined);
      return;
    }

    if (!supportedImageTypes.has(file.type)) {
      setError("Please choose a JPG, JPEG or PNG image.");
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      return;
    }

    setUploading(true);

    try {
      const imageUrl = await uploadFile(file);
      setUrl(imageUrl);
      onChange?.(imageUrl);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Upload failed");
    }

    setUploading(false);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return (
    <div
      className={twMerge("flex flex-wrap items-center gap-3 p-4", className)}
    >
      <div>
        {error && (
          <div className="mb-2 text-sm font-medium text-red-600">{error}</div>
        )}
        <div
          className={twMerge(
            "flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-zinc-100",
            previewClassName,
          )}
        >
          {uploading ? (
            <Spinner />
          ) : url ? (
            <img
              alt="Uploaded preview"
              src={url}
              className="h-full w-full object-cover"
            />
          ) : null}
        </div>
      </div>
      <div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          className="hidden"
          onChange={(event) => {
            void setFile(event.target.files?.[0]);
          }}
        />
        <div className="flex gap-2">
          <Button
            color="secondary"
            type="button"
            onClick={() => inputRef.current?.click()}
          >
            Upload file
          </Button>
          {url && onRemove && (
            <Button color="inverse-red" type="button" onClick={onRemove}>
              Remove
            </Button>
          )}
        </div>
        <p className="mt-2 text-sm text-zinc-500">
          Supported file types: JPG, JPEG and PNG. Maximum file size: 5 MB.
        </p>
      </div>
    </div>
  );
};
